/**
 * Iter59 — Helpers, constantes y subcomponentes compartidos entre los tabs
 * de la ficha de cliente (extraídos del antiguo monolito ClienteDetalle.jsx).
 */
import React from 'react';
import { TreeNode } from 'react-organizational-chart';
import {
  Eye, Plus, Pencil, Trash2, UserPlus, UserCog, CheckCircle2,
  FileText, Mail, Clock,
} from 'lucide-react';

// --- Taxonomía alineada con backend ---
export const TIPOS = [
  { v: 'distribuidor',           l: 'Distribuidor' },
  { v: 'gestora',                l: 'Gestora' },
  { v: 'administracion_publica', l: 'Admin. Pública' },
  { v: 'fabricante',             l: 'Fabricante' },
  { v: 'otro',                   l: 'Otro' },
];

export const ESTADOS = [
  { v: 'lead',      l: 'Lead' },
  { v: 'prospecto', l: 'Prospecto' },
  { v: 'activo',    l: 'Activo' },
  { v: 'inactivo',  l: 'Inactivo' },
];

export const NATURALEZAS = [
  { v: '',         l: '— Sin especificar —' },
  { v: 'publica',  l: 'Pública' },
  { v: 'privada',  l: 'Privada' },
  { v: 'mixta',    l: 'Mixta' },
];

export const AMBITOS = [
  { v: '',            l: '— Sin especificar —' },
  { v: 'local',       l: 'Local' },
  { v: 'provincial',  l: 'Provincial' },
  { v: 'autonomico',  l: 'Autonómico' },
  { v: 'nacional',    l: 'Nacional' },
];

export const ESTADO_STYLE = {
  lead: 'bg-amber-50 border-amber-200 text-amber-700',
  prospecto: 'bg-blue-50 border-blue-200 text-blue-700',
  activo: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  inactivo: 'bg-ink-100 border-ink-200 text-ink-500',
};

export const ESTADO_CONTRATO_STYLE = {
  prospecto:     'bg-blue-50 border-blue-200 text-blue-700',
  en_licitacion: 'bg-amber-50 border-amber-200 text-amber-700',
  activo:        'bg-emerald-50 border-emerald-200 text-emerald-700',
  prorrogado:    'bg-sky-50 border-sky-200 text-sky-700',
  finalizado:    'bg-ink-100 border-ink-200 text-ink-500',
};

export const TIPO_LABEL = Object.fromEntries(TIPOS.map((t) => [t.v, t.l]));

export const NIVELES_RELACION = [
  { v: 'sin_definir', l: 'Sin definir',        color: 'bg-ink-200',     dot: 'bg-ink-300' },
  { v: 'verde',       l: 'Amistad estrecha',   color: 'bg-emerald-500', dot: 'bg-emerald-500' },
  { v: 'amarillo',    l: 'Cordial',            color: 'bg-amber-400',   dot: 'bg-amber-400' },
  { v: 'rojo',        l: 'Debe mejorar',       color: 'bg-red-500',     dot: 'bg-red-500' },
];

export const emptyEntidad = () => ({
  razon_social: '', nombre_comercial: '', cif: '', codigo_cliente_erp: '',
  tipo: 'otro', estado: 'prospecto', grupo_id: '',
  es_cliente: true, es_proveedor: false,
  naturaleza_juridica: '', es_ute: false, ambito_distribuidor: '',
  materiales_fabricados: [],
  oficina_tecnica_notas: '',
  provincia: '', pais: 'ES', localidad: '', direccion: '', codigo_postal: '',
  telefono: '', email: '', web: '',
  division: '',
  comercial_asignado_id: '', notas: '',
  // Delegaciones
  es_delegacion: false,
  parent_entidad_id: '',
  tiene_deposito_nuestro: false,
  es_almacen_logistico: false,
});

export const emptyContacto = () => ({
  nombre: '', apellidos: '', cargo: '', departamento: '',
  email_trabajo: '', telefono_trabajo: '', movil_trabajo: '',
  email_personal: '', telefono_personal: '', movil_personal: '',
  es_decisor: false, fecha_inicio_empleo: '', jefe_directo_id: '',
  nivel_relacion: 'sin_definir',
  division: '',
  notas: '',
});

// --- Subcomponentes UI compartidos ---
export const Campo = ({ label, children, wide, required }) => (
  <div className={wide ? 'sm:col-span-2 md:col-span-2' : ''}>
    <label className="text-xs font-medium text-ink-600 mb-1 block">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

export const Select = ({ value, onChange, options, disabled, testid }) => (
  <select
    value={value}
    disabled={disabled}
    onChange={(e) => onChange(e.target.value)}
    className="w-full min-h-[2.5rem] rounded-md border border-ink-200 bg-white text-sm px-3 py-2 text-ink-900 disabled:bg-ink-50 disabled:text-ink-500"
    data-testid={testid}
  >
    {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
  </select>
);

export const SubTab = ({ active, onClick, icon: Icon, label }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border-b-2 -mb-px transition-colors ${
      active ? 'border-brand-500 text-brand-700' : 'border-transparent text-ink-500 hover:text-ink-800'
    }`}
  >
    <Icon className="w-3.5 h-3.5" /> {label}
  </button>
);

// --- Organigrama ---
export function buildTree(contactos) {
  const byId = Object.fromEntries(contactos.map((c) => [c.id, { ...c, children: [] }]));
  const roots = [];
  contactos.forEach((c) => {
    if (c.jefe_directo_id && byId[c.jefe_directo_id]) {
      byId[c.jefe_directo_id].children.push(byId[c.id]);
    } else {
      roots.push(byId[c.id]);
    }
  });
  return roots;
}

const NIVEL_COLOR = {
  verde: 'bg-emerald-500 ring-2 ring-white',
  amarillo: 'bg-amber-400 ring-2 ring-white',
  rojo: 'bg-red-500 ring-2 ring-white',
  sin_definir: 'bg-ink-300 ring-2 ring-white',
};
const NIVEL_TITLE = {
  verde: 'Amistad estrecha',
  amarillo: 'Cordial',
  rojo: 'Debe mejorar',
  sin_definir: 'Sin definir',
};

export const NivelDot = ({ nivel = 'sin_definir', small = false }) => (
  <span
    title={NIVEL_TITLE[nivel] || 'Sin definir'}
    className={`absolute -bottom-0.5 -right-0.5 ${small ? 'w-2.5 h-2.5' : 'w-3.5 h-3.5'} rounded-full ${NIVEL_COLOR[nivel] || NIVEL_COLOR.sin_definir}`}
  />
);

export const OrgCard = ({ contacto, onClick }) => {
  const inicial = (contacto.nombre || '?')[0]?.toUpperCase();
  const nombreCompleto = `${contacto.nombre} ${contacto.apellidos || ''}`.trim();
  const ringByNivel = {
    verde: 'ring-emerald-300', amarillo: 'ring-amber-300', rojo: 'ring-rose-300', sin_definir: 'ring-ink-200',
  }[contacto.nivel_relacion || 'sin_definir'];
  return (
    <button
      onClick={onClick}
      className={`group inline-flex items-center gap-2.5 px-3 py-2 rounded-lg border border-ink-200 bg-white shadow-sm hover:shadow-md hover:border-brand-300 transition ring-1 ${ringByNivel}`}
      data-testid={`org-node-${contacto.id}`}
      title="Click para editar este contacto"
    >
      <div className="h-8 w-8 rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-white text-sm flex items-center justify-center font-bold shrink-0">
        {inicial}
      </div>
      <div className="text-left">
        <p className="font-semibold text-ink-900 text-sm leading-tight whitespace-nowrap">{nombreCompleto}</p>
        {contacto.cargo && (
          <p className="text-[11px] text-ink-500 leading-tight whitespace-nowrap">
            {contacto.cargo}
            {contacto.es_decisor && <span className="ml-1 text-amber-600">★</span>}
          </p>
        )}
      </div>
    </button>
  );
};

export const OrgBranch = ({ node, onClick }) => (
  <TreeNode label={<OrgCard contacto={node} onClick={() => onClick(node)} />}>
    {node.children.map((child) => <OrgBranch key={child.id} node={child} onClick={onClick} />)}
  </TreeNode>
);

// --- Actividad ---
const ACT_ICONS = {
  'eye': Eye, 'plus': Plus, 'pencil': Pencil, 'trash': Trash2,
  'user-plus': UserPlus, 'user-cog': UserCog, 'user-x': Trash2,
  'check': CheckCircle2, 'file': FileText, 'mail': Mail, 'clock': Clock,
};

export const fmtRel = (iso) => {
  try {
    const now = new Date(); const d = new Date(iso);
    const s = Math.floor((now - d) / 1000);
    if (s < 60) return `hace ${s}s`;
    if (s < 3600) return `hace ${Math.floor(s / 60)} min`;
    if (s < 86400) return `hace ${Math.floor(s / 3600)}h`;
    if (s < 86400 * 7) return `hace ${Math.floor(s / 86400)} días`;
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

export const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

export const ActividadItem = ({ item }) => {
  const Icon = ACT_ICONS[item.icon] || Clock;
  return (
    <li className="flex gap-4" data-testid="actividad-item">
      <div className="h-10 w-10 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-ink-900">
          <span className="font-semibold">{item.usuario_email || 'Alguien'}</span>
          <span className="text-ink-500"> · {item.rol || '—'}</span>
        </p>
        <p className="text-sm text-ink-700">
          {item.label}
          {item.extra && <span className="text-ink-500"> ({item.extra})</span>}
        </p>
        <p className="text-xs text-ink-400 mt-0.5">{fmtRel(item.timestamp)}</p>
      </div>
    </li>
  );
};
